'use client';

/**
 * UserStuckFundsCard Component
 *
 * Displays a single stuck funds position with rescue button.
 * Shows underlying token amounts and USD values.
 */
import { Card, CardBody, Button, Chip, Tooltip, Skeleton } from '@heroui/react';
import { AlertTriangle, Coins, Shovel, Info, ExternalLink, Share2, DollarSign } from 'lucide-react';
import { useChainId } from 'wagmi';
import { useCallback, useMemo } from 'react';
import { chainSettings, type ChainType } from '@/constants/chain-specific';
import type { UnderlyingAsset } from '@/lib/masterchef-cache/types';
import type { TokenPrice } from '@/lib/pricing-service/pricing-service';
import { useUnderlyingAmounts } from '@/hooks/masterchef/useUnderlyingAmounts';
interface StuckFundsPosition {
  protocol: {
    name: string;
    slug: string;
    status: string;
    statusReason?: string;
  };
  contract: {
    contractId: string;
    label: string;
    contractAddress: `0x${string}`;
    chainId: number;
  };
  poolId: number;
  lpTokenAddress: `0x${string}`;
  lpTokenSymbol?: string;
  lpTokenDecimals: number;
  stakedAmount: bigint;
  stakedAmountFormatted: string;
  pendingRewards?: bigint;
  underlyingAssets: UnderlyingAsset[];
  withdrawConfig: {
    standardWithdrawAbi: any;
    emergencyWithdrawAbi?: any;
    recommendation: 'standard' | 'emergency';
  };
}
interface UserStuckFundsCardProps {
  position: StuckFundsPosition;
  onRecover: () => void;
  /** Whether we're checking a friend's wallet */
  isCheckingFriend?: boolean;
  /** The wallet address being scanned (for sharing) */
  scannedAddress?: string;
  /** Token prices map (address -> TokenPrice) */
  tokenPrices?: Map<string, TokenPrice>;
  /** Whether pricing is loading */
  isPricingLoading?: boolean;
}

// Format token amount for display
function formatTokenAmount(amount: string): string {
  const num = parseFloat(amount);
  if (num === 0) return '0';
  if (num < 0.0001) return '<0.0001';
  if (num < 1) return num.toFixed(4);
  if (num < 1000) return num.toFixed(2);
  if (num < 1000000) return `${(num / 1000).toFixed(2)}K`;
  return `${(num / 1000000).toFixed(2)}M`;
}
export default function UserStuckFundsCard({
  position,
  onRecover,
  isCheckingFriend,
  tokenPrices,
  isPricingLoading
}: UserStuckFundsCardProps) {
  const chainId = useChainId();
  const chain = chainSettings[position.contract.chainId as ChainType];
  const explorerUrl = chain?.blockExplorerUrl;

  // Get underlying token amounts from LP position
  const {
    amounts: underlyingAmounts,
    isLoading: isLoadingAmounts
  } = useUnderlyingAmounts({
    lpTokenAddress: position.lpTokenAddress,
    userLpBalance: position.stakedAmount,
    underlyingAssets: position.underlyingAssets,
    chainId: position.contract.chainId,
    enabled: position.underlyingAssets.length > 0
  });

  // Calculate total USD value based on actual underlying amounts
  const {
    totalValueUsd,
    tokenValues
  } = useMemo(() => {
    if (!tokenPrices || underlyingAmounts.length === 0) {
      return {
        totalValueUsd: null,
        tokenValues: []
      };
    }
    let total = 0;
    const values: {
      symbol: string;
      amount: string;
      valueUsd: number | null;
    }[] = [];
    for (const {
      asset,
      amountFormatted
    } of underlyingAmounts) {
      const price = tokenPrices.get(asset.address.toLowerCase());
      const amount = parseFloat(amountFormatted);
      const valueUsd = price?.price_usd ? amount * parseFloat(price.price_usd) : null;
      values.push({
        symbol: asset.symbol,
        amount: amountFormatted,
        valueUsd
      });
      if (valueUsd !== null) {
        total += valueUsd;
      }
    }
    return {
      totalValueUsd: values.some(v => v.valueUsd !== null) ? total : null,
      tokenValues: values
    };
  }, [tokenPrices, underlyingAmounts]);

  // Handle share with Web Share API
  const handleShare = useCallback(async () => {
    const rescueUrl = 'https://app.chewyswap.dog/rescue';
    const shareData = {
      title: 'Recover Your Stuck Funds!',
      text: `Hey! I found some of your tokens stuck in abandoned DeFi protocols. Check out ChewySwap's Fund Recovery tool to rescue them!`,
      url: rescueUrl
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.text}\n\n${rescueUrl}`);
        // Could add a toast notification here
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      // User cancelled or error
      if ((error as Error).name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    }
  }, []);
  const isEmergencyRecommended = position.withdrawConfig.recommendation === 'emergency';

  // Format staked amount for display
  const displayAmount = Number(position.stakedAmountFormatted) > 0.0001 ? Number(position.stakedAmountFormatted).toFixed(4) : '<0.0001';
  return <Card className={`
        relative overflow-hidden border-2
        ${isEmergencyRecommended ? 'border-danger' : 'border-warning'}
        bg-gradient-to-br from-content1 to-content2
      `}>
      {/* Warning stripe for emergency */}
      {isEmergencyRecommended && <div className="absolute top-0 left-0 right-0 h-1 bg-danger animate-pulse" />}

      <CardBody className="gap-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-lg">{position.protocol.name}</h3>
              <Chip size="sm" color={position.protocol.status === 'rugged' ? 'danger' : 'warning'} variant="flat">
                {position.protocol.status}
              </Chip>
            </div>
            <p className="text-sm text-foreground-500">
              {position.contract.label} · Pool #{position.poolId}
            </p>
          </div>

          {/* Explorer link */}
          {explorerUrl && <Tooltip content="View contract on explorer">
              <a href={`${explorerUrl}/address/${position.contract.contractAddress}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-content2 rounded-lg transition-colors">
                <ExternalLink className="w-4 h-4 text-foreground-400" />
              </a>
            </Tooltip>}
        </div>

        {/* LP Token info */}
        <div className="flex items-center gap-4 p-3 bg-content2 rounded-xl">
          <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center">
            <Coins className="w-6 h-6 text-warning" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">{displayAmount}</span>
              <span className="text-foreground-500">
                {position.lpTokenSymbol || 'LP Tokens'}
              </span>
            </div>
            {position.underlyingAssets.length > 0 && <p className="text-sm text-foreground-400 mt-1">
                {position.underlyingAssets.map(a => a.symbol).join(' / ')}
              </p>}
          </div>
        </div>

        {/* Underlying Token Amounts */}
        {position.underlyingAssets.length > 0 && <div className="p-3 bg-content2/50 rounded-xl space-y-2">
            <p className="text-xs text-foreground-400 font-medium uppercase tracking-wide">
              Your Share
            </p>
            {isLoadingAmounts ? <div className="space-y-2">
                <Skeleton className="h-5 w-32 rounded-md" />
                <Skeleton className="h-5 w-28 rounded-md" />
              </div> : underlyingAmounts.length > 0 ? <div className="space-y-1">
                {underlyingAmounts.map(({
            asset: asset_0,
            amountFormatted: amountFormatted_0
          }) => {
            const tokenValue = tokenValues.find(v_0 => v_0.symbol === asset_0.symbol);
            return <div key={asset_0.address} className="flex justify-between items-center">
                      <span className="text-foreground font-medium">
                        {formatTokenAmount(amountFormatted_0)} {asset_0.symbol}
                      </span>
                      {isPricingLoading ? <Skeleton className="h-4 w-16 rounded-md" /> : tokenValue?.valueUsd !== null && tokenValue?.valueUsd !== undefined ? <span className="text-sm text-foreground-400">
                          ${tokenValue.valueUsd.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                        </span> : null}
                    </div>;
          })}
              </div> : <p className="text-sm text-foreground-400">
                {position.underlyingAssets.map(a_0 => a_0.symbol).join(' / ')}
              </p>}
          </div>}

        {/* Total USD Value */}
        {(isPricingLoading || isLoadingAmounts || totalValueUsd !== null) && <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg border border-success/30">
            <DollarSign className="w-5 h-5 text-success flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-foreground-500">Total Value</p>
              {isPricingLoading || isLoadingAmounts ? <Skeleton className="h-6 w-24 rounded-md" /> : <p className="text-lg font-bold text-success">
                  {totalValueUsd !== null ? `$${totalValueUsd.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}` : 'Price unavailable'}
                </p>}
            </div>
          </div>}

        {/* Warning for emergency withdraw */}
        {isEmergencyRecommended && <div className="flex items-start gap-2 p-3 bg-danger/10 rounded-lg border border-danger/30">
            <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-danger font-medium">
                Emergency Withdraw Recommended
              </p>
              <p className="text-xs text-foreground-500 mt-1">
                This protocol may have issues with standard withdrawals.
                Emergency withdraw forfeits pending rewards but ensures token
                recovery.
              </p>
            </div>
          </div>}

        {/* Pending rewards info */}
        {position.pendingRewards && position.pendingRewards > 0n && <div className="flex items-center gap-2 text-sm text-foreground-500">
            <Info className="w-4 h-4" />
            <span>
              Pending rewards will be claimed with standard withdraw
            </span>
          </div>}

        {/* Action buttons */}
        {isCheckingFriend ? <div className="space-y-3">
            <div className="p-3 bg-success/10 rounded-lg border border-success/30 text-center">
              <p className="text-sm text-success font-medium">
                Great find! Share this with your fren so they can recover their funds.
              </p>
            </div>
            <Button fullWidth color="primary" className="font-semibold" onPress={handleShare} startContent={<Share2 className="w-5 h-5" />}>
              Share with Fren
            </Button>
          </div> : <Button fullWidth color={isEmergencyRecommended ? 'danger' : 'warning'} className="font-semibold text-black" onPress={onRecover} startContent={<Shovel className="w-5 h-5" />}>
            Rescue Funds
          </Button>}
      </CardBody>
    </Card>;
}